// CustomHeader.tsx
import clsx from 'clsx'
import { FC, PropsWithChildren, useMemo } from 'react'
import { HeaderProps } from 'react-table'
import { initialQueryState } from '../../../../../../../../_metronic/helpers'
import { useQueryRequest } from '../../core/QueryRequestProvider'
import { Data } from '../../core/_models'

// Lucide Icons
import {
  Hash,
  User,
  Phone,
  Info,
  Settings,
  FileText,
  Tag,
  ArrowUp,
  ArrowDown,
} from 'lucide-react'

type Props = {
  className?: string
  title?: string
  iconName?: string
  tableProps: PropsWithChildren<HeaderProps<Data>>
}

// Map iconName ke Lucide
const ICONS = {
  hash: Hash,
  user: User,
  phone: Phone,
  info: Info,
  settings: Settings,
  'file-text': FileText,
  tag: Tag,
}

const CustomHeader: FC<Props> = ({ className, title, iconName, tableProps }) => {
  const id = tableProps.column.id
  const { state, updateState } = useQueryRequest()

  const isSelectedForSorting = useMemo(() => state.sort === id, [state, id])
  const order: 'asc' | 'desc' | undefined = useMemo(() => state.order, [state])

  const sortColumn = () => {
    if (id === 'actions' || id === 'selection') return

    if (!isSelectedForSorting) {
      updateState({ sort: id, order: 'asc', ...initialQueryState })
      return
    }

    if (isSelectedForSorting && order !== undefined) {
      if (order === 'asc') {
        updateState({ sort: id, order: 'desc', ...initialQueryState })
        return
      }
      updateState({ sort: undefined, order: undefined, ...initialQueryState })
    }
  }

  const IconComponent = iconName ? ICONS[iconName as keyof typeof ICONS] : null

  return (
    <th
      {...tableProps.column.getHeaderProps()}
      className={clsx(className, 'lov-table__header-cell', isSelectedForSorting && order && `table-sort-${order}`)}
      style={{ cursor: 'pointer' }}
      onClick={sortColumn}
    >
      <div className="d-flex align-items-center justify-content-center">
        {IconComponent && <IconComponent size={16} strokeWidth={1.5} className="me-2" />}
        {title}
        {isSelectedForSorting && order && (
          <span className="ms-1">{order === 'asc' ? <ArrowUp size={12} /> : <ArrowDown size={12} />}</span>
        )}
      </div>
    </th>
  )
}

export { CustomHeader }
